import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Clock,
  MapPin,
  Package,
  User,
  FileText,
  Edit,
  RefreshCw,
  StickyNote,
} from "lucide-react";
import { LoadEvent } from "@/api/commonSchemas";

interface LoadEventCardProps {
  events: LoadEvent[];
  title?: string;
  maxHeight?: string;
  showIcons?: boolean;
  variant?: "default" | "compact" | "detailed";
  className?: string;
}

const getEventIcon = (eventType: string) => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    Created: Package,
    "Location Updated": MapPin,
    "Carrier Updated": User,
    "Details Updated": Edit,
    "Notes Updated": StickyNote,
    "State Changed": RefreshCw,
  };

  return iconMap[eventType] || FileText;
};

const getEventColor = (eventType: string) => {
  const colorMap: Record<string, string> = {
    Created: "bg-blue-50 border-blue-200 text-blue-800",
    "Location Updated": "bg-purple-50 border-purple-200 text-purple-800",
    "Carrier Updated": "bg-green-50 border-green-200 text-green-800",
    "Details Updated": "bg-orange-50 border-orange-200 text-orange-800",
    "Notes Updated": "bg-yellow-50 border-yellow-200 text-yellow-800",
    "State Changed": "bg-indigo-50 border-indigo-200 text-indigo-800",
  };

  return colorMap[eventType] || "bg-gray-50 border-gray-200 text-gray-800";
};

const getBadgeVariant = (
  eventType: string
): "default" | "secondary" | "destructive" | "outline" => {
  if (["Created", "Carrier Updated"].includes(eventType)) return "default";
  if (["State Changed"].includes(eventType)) return "secondary";
  return "outline";
};

const formatDate = (dateTime: Date) => {
  return new Date(dateTime).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTimeAgo = (dateTime: Date) => {
  const now = new Date();
  const diffInHours = Math.floor(
    (now.getTime() - dateTime.getTime()) / (1000 * 60 * 60)
  );

  if (diffInHours < 1) return "Just now";
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
  return formatDate(dateTime);
};

const getMetadataDisplay = (event: LoadEvent) => {
  if (!event.metadata) return null;

  switch (event.eventType) {
    case "Location Updated":
      const locationData = event.metadata as { city: string; state: string };
      return (
        <span className="flex items-center text-sm gap-1">
          <MapPin className="w-3 text-sm h-3" />
          <p>
            {locationData.city}, {locationData.state}
          </p>
        </span>
      );
    case "State Changed":
      const stateData = event.metadata as { newState: string };
      return (
        <Badge variant="outline" className="text-xs">
          → {stateData.newState}
        </Badge>
      );
    default:
      return null;
  }
};

export const LoadEventCard: React.FC<LoadEventCardProps> = ({
  events,
  title = "Load Events",
  maxHeight = "222px",
  showIcons = true,
  variant = "default",
  className = "overflow-hidden pb-4",
}) => {
  if (variant === "compact") {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <CardContent className={`pb-0 overflow-y-auto`} style={{ maxHeight }}>
          {events.length > 0 ? (
            events.map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded border"
              >
                <div className="flex items-center gap-2">
                  {showIcons && (
                    <div className="w-6 h-6 flex items-center justify-center">
                      {React.createElement(getEventIcon(event.eventType), {
                        className: "w-3 h-3 text-muted-foreground",
                      })}
                    </div>
                  )}
                  <span className="text-xs font-medium">{event.eventType}</span>
                  {getMetadataDisplay(event)}
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatTimeAgo(event.createdAt)}
                </span>
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">
              No events recorded for this load.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  if (variant === "detailed") {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {title}
            <Badge variant="outline" className="ml-auto">
              {events.length} events
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className={`pb-0 overflow-y-auto`} style={{ maxHeight }}>
          {events.length > 0 ? (
            events.map((event, index) => {
              const IconComponent = getEventIcon(event.eventType);
              const isLast = index === events.length - 1;

              return (
                <div key={event.id} className="relative">
                  {/* Timeline line */}
                  {!isLast && (
                    <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-200" />
                  )}

                  <div
                    className={`p-4 rounded-lg border ${getEventColor(
                      event.eventType
                    )}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white border-2 border-current flex items-center justify-center">
                        <IconComponent className="w-5 h-5" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <Badge
                            variant={getBadgeVariant(event.eventType)}
                            className="text-xs"
                          >
                            {event.eventType}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(event.createdAt)}
                          </span>
                        </div>

                        <p className="text-sm font-medium mb-1">
                          {event.description}
                        </p>

                        {/* Additional details from metadata */}
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          {getMetadataDisplay(event)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">
                No events recorded for this load.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Default variant
  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className={`pb-0 overflow-y-auto`} style={{ maxHeight }}>
        {events.length > 0 ? (
          <ul className="space-y-2">
            {events.map((event) => (
              <li
                key={event.id}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {showIcons && (
                      <div className="w-8 h-8 rounded-full bg-white border flex items-center justify-center flex-shrink-0">
                        {React.createElement(getEventIcon(event.eventType), {
                          className: "w-4 h-4 text-muted-foreground",
                        })}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900">
                          {event.eventType}
                        </p>
                      </div>
                      <p>{getMetadataDisplay(event)}</p>
                      <p className="text-sm text-gray-600">
                        {event.description}
                      </p>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0 ml-2">
                    <span className="text-xs text-gray-500">
                      {formatDate(event.createdAt)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-6">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No events recorded for this load.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LoadEventCard;
